import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { ensureRecentAdminReauth } from '@/lib/adminSecurity';
import { toast } from 'sonner';
import { Bell, Copy, Eye, Image as ImageIcon, Loader2, Megaphone, Plus, Send, TriangleAlert } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LoadingOverlay } from '@/components/ui/loading-overlay';

const buildEmptyForm = () => ({
  name: '',
  push_type: 'general',
  content_variant: 'text',
  title: '',
  body: '',
  image_url: '',
  cta_label: '',
  cta_screen: '',
  data_payload: [{ key: 'type', value: 'general' }],
  roles: [],
  admin_ids: [],
  staff_ids: [],
  customer_ids: [],
  include_assigned_staff_for_customers: false,
  include_admin_watchers: false,
});

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusClasses = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
  partially_sent: 'bg-amber-100 text-amber-700',
};

const arrayToggle = (list, value, checked) => {
  if (checked) return Array.from(new Set([...(list || []), value]));
  return (list || []).filter((item) => item !== value);
};

const normalizePayloadRows = (rows) => {
  const payload = {};
  (rows || []).forEach((row) => {
    const key = String(row?.key || '').trim();
    const value = String(row?.value || '').trim();
    if (!key || !value) return;
    payload[key] = value;
  });
  return payload;
};

const contentVariantMeta = {
  text: {
    label: 'Text Only',
    description: 'Simple title and message with optional payload data.',
    icon: Megaphone,
  },
  image_cta: {
    label: 'Image + CTA',
    description: 'Banner image with a CTA button inside the mobile app.',
    icon: ImageIcon,
  },
  priority_alert: {
    label: 'Priority Alert',
    description: 'Urgent alert with stronger visual emphasis and CTA.',
    icon: TriangleAlert,
  },
};

const MOBILE_CTA_DESTINATIONS = [
  { value: '/(tabs)/dashboard', label: 'Dashboard', description: 'Open dashboard tab' },
  { value: '/(tabs)/orders', label: 'Orders', description: 'Open orders tab' },
  { value: '/(tabs)/customers', label: 'Customers', description: 'Open customers tab' },
  { value: '/(tabs)/payments', label: 'Payments', description: 'Open payments tab' },
  { value: '/(tabs)/tasks', label: 'Tasks', description: 'Open tasks tab' },
  { value: '/(tabs)/leads', label: 'Leads', description: 'Open leads tab' },
  { value: '/(tabs)/notifications', label: 'Notifications', description: 'Open notifications tab' },
  { value: '/day-planner', label: 'Day Planner', description: 'Open day planner screen' },
  { value: '/expenses', label: 'Expenses', description: 'Open expenses screen' },
  { value: '/holidays', label: 'Holidays', description: 'Open holidays screen' },
  { value: '/leave', label: 'Leave', description: 'Open leave screen' },
  { value: '/orders-list', label: 'Orders List', description: 'Open full orders list screen' },
  { value: '/new-customer', label: 'New Customer', description: 'Open new customer form' },
  { value: '/new-order', label: 'New Order', description: 'Open new order form' },
  { value: '/new-payment', label: 'New Payment', description: 'Open new payment form' },
  { value: '/pricing', label: 'Pricing', description: 'Open pricing index' },
  { value: '/order/{order_id}', label: 'Order Detail', description: 'Requires payload order_id' },
  { value: '/customer/{customer_id}', label: 'Customer Detail', description: 'Requires payload customer_id' },
  { value: '/lead/{lead_id}', label: 'Lead Detail', description: 'Requires payload lead_id' },
  { value: '/pricing/{variant_id}', label: 'Pricing Detail', description: 'Requires payload variant_id' },
];

export default function PushNotificationsPage() {
  const [activeTab, setActiveTab] = useState('campaigns');
  const [loading, setLoading] = useState(true);
  const [savingDraft, setSavingDraft] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [sendingExistingId, setSendingExistingId] = useState('');
  const [resendingId, setResendingId] = useState('');
  const [estimatingAudience, setEstimatingAudience] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [meta, setMeta] = useState({
    push_types: [],
    admins: [],
    staff: [],
    customers: [],
    targetable_roles: [],
    future_capabilities: {},
  });
  const [form, setForm] = useState(buildEmptyForm());
  const [audienceEstimate, setAudienceEstimate] = useState(null);
  const [adminSearch, setAdminSearch] = useState('');
  const [staffSearch, setStaffSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignRes, metaRes] = await Promise.all([
        adminApi.getPushCampaigns(),
        adminApi.getPushMeta(),
      ]);
      setCampaigns(campaignRes.data || []);
      setMeta(metaRes.data || {});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load push notifications module');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const pushTypeMap = useMemo(() => {
    const map = {};
    (meta.push_types || []).forEach((entry) => {
      map[entry.key] = entry;
    });
    return map;
  }, [meta.push_types]);

  useEffect(() => {
    const typeDef = pushTypeMap[form.push_type];
    if (!typeDef) return;
    setForm((prev) => {
      const nextRows = [...(prev.data_payload || [])];
      const hasTypeRow = nextRows.some((row) => String(row?.key || '').trim() === 'type');
      if (!hasTypeRow) {
        nextRows.unshift({ key: 'type', value: prev.push_type });
      } else {
        nextRows.forEach((row) => {
          if (String(row?.key || '').trim() === 'type') row.value = prev.push_type;
        });
      }
      return {
        ...prev,
        title: prev.title || typeDef.default_title || '',
        body: prev.body || typeDef.default_body || '',
        data_payload: nextRows,
      };
    });
  }, [form.push_type, pushTypeMap]);

  const filteredAdmins = useMemo(() => {
    const term = adminSearch.trim().toLowerCase();
    return (meta.admins || []).filter((entry) => {
      if (!term) return true;
      return `${entry.name} ${entry.username} ${entry.role}`.toLowerCase().includes(term);
    });
  }, [meta.admins, adminSearch]);

  const filteredStaff = useMemo(() => {
    const term = staffSearch.trim().toLowerCase();
    return (meta.staff || []).filter((entry) => {
      if (!term) return true;
      return `${entry.name} ${entry.phone} ${entry.area}`.toLowerCase().includes(term);
    });
  }, [meta.staff, staffSearch]);

  const filteredCustomers = useMemo(() => {
    const term = customerSearch.trim().toLowerCase();
    return (meta.customers || []).filter((entry) => {
      if (!term) return true;
      return `${entry.name} ${entry.shop_name} ${entry.phone} ${entry.sales_staff_name}`.toLowerCase().includes(term);
    });
  }, [meta.customers, customerSearch]);

  const audiencePayload = useMemo(() => ({
    target_filters: {
      roles: form.roles,
      admin_ids: form.admin_ids,
      staff_ids: form.staff_ids,
      customer_ids: form.customer_ids,
      include_assigned_staff_for_customers: form.include_assigned_staff_for_customers,
      include_admin_watchers: form.include_admin_watchers,
    },
  }), [form]);

  useEffect(() => {
    const hasAudience =
      form.roles.length ||
      form.admin_ids.length ||
      form.staff_ids.length ||
      form.customer_ids.length ||
      form.include_assigned_staff_for_customers ||
      form.include_admin_watchers;
    if (!hasAudience) {
      setAudienceEstimate(null);
      return;
    }
    const timer = setTimeout(async () => {
      setEstimatingAudience(true);
      try {
        const response = await adminApi.estimatePushAudience(audiencePayload);
        setAudienceEstimate(response.data || null);
      } catch {
        setAudienceEstimate(null);
      } finally {
        setEstimatingAudience(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [audiencePayload, form.roles.length, form.admin_ids.length, form.staff_ids.length, form.customer_ids.length, form.include_assigned_staff_for_customers, form.include_admin_watchers]);

  const resetForm = () => {
    setForm(buildEmptyForm());
    setAudienceEstimate(null);
    setAdminSearch('');
    setStaffSearch('');
    setCustomerSearch('');
  };

  const buildCampaignPayload = () => ({
    name: form.name,
    push_type: form.push_type,
    content_variant: form.content_variant,
    title: form.title,
    body: form.body,
    image_url: form.content_variant === 'text' ? '' : form.image_url,
    cta_label: form.content_variant === 'text' ? '' : form.cta_label,
    cta_screen: form.content_variant === 'text' ? '' : form.cta_screen,
    data_payload: normalizePayloadRows(form.data_payload),
    target_filters: audiencePayload.target_filters,
  });

  const validateSendReady = () => {
    if (!String(form.title || '').trim()) {
      toast.error('Push title is required before sending');
      return false;
    }
    if (!String(form.body || '').trim()) {
      toast.error('Push body is required before sending');
      return false;
    }
    if (form.content_variant === 'image_cta' && !String(form.image_url || '').trim()) {
      toast.error('Image URL is required for Image + CTA pushes');
      return false;
    }
    if ((form.content_variant === 'image_cta' || form.content_variant === 'priority_alert') && !String(form.cta_label || '').trim()) {
      toast.error('CTA label is required for this rich push');
      return false;
    }
    if ((form.content_variant === 'image_cta' || form.content_variant === 'priority_alert') && !String(form.cta_screen || '').trim()) {
      toast.error('CTA destination is required for this rich push');
      return false;
    }
    const hasAudience =
      form.roles.length ||
      form.admin_ids.length ||
      form.staff_ids.length ||
      form.customer_ids.length ||
      form.include_assigned_staff_for_customers ||
      form.include_admin_watchers;
    if (!hasAudience) {
      toast.error('Select at least one audience before sending');
      return false;
    }
    return true;
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      await adminApi.createPushCampaign(buildCampaignPayload());
      toast.success('Push draft saved');
      resetForm();
      setActiveTab('campaigns');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleCreateAndSend = async () => {
    if (!validateSendReady()) return;
    setSendingNow(true);
    try {
      const confirmed = await ensureRecentAdminReauth();
      if (!confirmed) return;
      const draftRes = await adminApi.createPushCampaign(buildCampaignPayload());
      const campaignId = draftRes.data?.id;
      if (!campaignId) throw new Error('Draft creation failed');
      const sendRes = await adminApi.sendPushCampaign(campaignId);
      toast.success(sendRes.data?.message || 'Push campaign processed');
      resetForm();
      setActiveTab('campaigns');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to send push');
    } finally {
      setSendingNow(false);
    }
  };

  const handleSendExisting = async (campaignId) => {
    setSendingExistingId(campaignId);
    try {
      const confirmed = await ensureRecentAdminReauth();
      if (!confirmed) return;
      const response = await adminApi.sendPushCampaign(campaignId);
      toast.success(response.data?.message || 'Push campaign processed');
      await fetchData();
      if (selectedCampaign?.id === campaignId) {
        const detail = await adminApi.getPushCampaignById(campaignId);
        setSelectedCampaign(detail.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send campaign');
    } finally {
      setSendingExistingId('');
    }
  };

  const handleResendCampaign = async (campaignId) => {
    setResendingId(campaignId);
    try {
      const confirmed = await ensureRecentAdminReauth();
      if (!confirmed) return;
      const duplicateRes = await adminApi.duplicatePushCampaign(campaignId);
      const duplicateId = duplicateRes.data?.id;
      if (!duplicateId) throw new Error('Campaign duplicate failed');
      const sendRes = await adminApi.sendPushCampaign(duplicateId);
      toast.success(sendRes.data?.message || 'Campaign duplicated and sent');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to resend campaign');
    } finally {
      setResendingId('');
    }
  };

  const openCampaign = async (campaignId) => {
    try {
      const response = await adminApi.getPushCampaignById(campaignId);
      setSelectedCampaign(response.data || null);
      setShowDetail(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load campaign');
    }
  };

  const deliveryCampaigns = useMemo(
    () => campaigns.filter((item) => item.status !== 'draft'),
    [campaigns]
  );

  const customerDeviceLive = Boolean(meta.future_capabilities?.customer_push_delivery_live);
  const selectedVariantMeta = contentVariantMeta[form.content_variant] || contentVariantMeta.text;
  const SelectedVariantIcon = selectedVariantMeta.icon;
  const ctaDestinationOptions = MOBILE_CTA_DESTINATIONS;
  const selectedCtaDestination = ctaDestinationOptions.some((entry) => entry.value === form.cta_screen)
    ? form.cta_screen
    : '__custom__';
  const selectedCtaDestinationMeta = ctaDestinationOptions.find((entry) => entry.value === form.cta_screen) || null;

  return (
    <div className="space-y-6" data-testid="push-notifications-page">
      <LoadingOverlay open={savingDraft || sendingNow} message={sendingNow ? 'Please wait. Push is being sent' : 'Please wait. Draft is being saved'} />
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Push Notifications</h1>
          <p className="text-[#A8A8A8] mt-1">Create, target, send, and review mobile push campaigns.</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setActiveTab('create');
          }}
          className="bg-[#C97A5E] hover:bg-[#B06850]"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Push
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#FAFAF9] border border-[#E5E5E5]">
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="create">Create Push</TabsTrigger>
          <TabsTrigger value="delivery">Delivery History</TabsTrigger>
          <TabsTrigger value="types">Templates / Types</TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns" className="space-y-6 mt-6">
          <Card className="bg-white border-[#E5E5E5]">
            <CardHeader>
              <CardTitle className="text-[#2D2420]">Campaigns</CardTitle>
              <CardDescription>Drafts and sent campaigns with delivery summary.</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
                </div>
              ) : campaigns.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-[#A8A8A8]">
                  <Bell className="w-5 h-5 mr-2" />
                  No push campaigns yet
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
	                      <tr>
	                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Name</th>
	                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Type</th>
	                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Style</th>
	                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Audience</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Created</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Sent</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Delivery</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {campaigns.map((campaign) => (
                        <tr key={campaign.id} className="hover:bg-[#FAFAF9]/60">
                          <td className="py-3 px-4">
                            <div className="font-medium text-[#2D2420]">{campaign.name}</div>
                            <div className="text-xs text-[#A8A8A8] line-clamp-1">{campaign.title}</div>
                          </td>
	                          <td className="py-3 px-4 text-[#2D2420]">{pushTypeMap[campaign.push_type]?.label || campaign.push_type}</td>
	                          <td className="py-3 px-4 text-[#A8A8A8]">{contentVariantMeta[campaign.content_variant]?.label || 'Text Only'}</td>
	                          <td className="py-3 px-4 text-[#A8A8A8] max-w-[260px]">{campaign.estimated_audience?.summary_text || '-'}</td>
                          <td className="py-3 px-4">
                            <Badge className={statusClasses[campaign.status] || 'bg-slate-100 text-slate-700'}>{campaign.status}</Badge>
                          </td>
                          <td className="py-3 px-4 text-[#A8A8A8]">{formatDateTime(campaign.created_at)}</td>
                          <td className="py-3 px-4 text-[#A8A8A8]">{formatDateTime(campaign.sent_at)}</td>
                          <td className="py-3 px-4 text-right text-[#2D2420]">
                            {(campaign.delivery_summary?.success_count || 0)}/{campaign.delivery_summary?.token_count || 0}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="outline" size="sm" onClick={() => openCampaign(campaign.id)}>
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              {campaign.status === 'draft' ? (
                                <Button size="sm" onClick={() => handleSendExisting(campaign.id)} disabled={sendingExistingId === campaign.id}>
                                  {sendingExistingId === campaign.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                                  Send
                                </Button>
                              ) : null}
                              {campaign.status !== 'draft' ? (
                                <Button variant="outline" size="sm" onClick={() => handleResendCampaign(campaign.id)} disabled={resendingId === campaign.id}>
                                  {resendingId === campaign.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                                  Resend
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="create" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 space-y-6">
              <Card className="bg-white border-[#E5E5E5]">
                <CardHeader>
                  <CardTitle className="text-[#2D2420]">Create Push</CardTitle>
                  <CardDescription>Prepare a custom or type-based campaign with structured payload and audience.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Campaign Name</Label>
                      <Input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="Month-end collection reminder" />
                    </div>
                    <div className="space-y-2">
                      <Label>Push Type</Label>
                      <Select value={form.push_type} onValueChange={(value) => setForm((prev) => ({ ...prev, push_type: value }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select push type" />
                        </SelectTrigger>
                        <SelectContent>
                          {(meta.push_types || []).map((type) => (
                            <SelectItem key={type.key} value={type.key}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} placeholder="Push title" />
                  </div>

	                  <div className="space-y-2">
	                    <Label>Message</Label>
	                    <Textarea value={form.body} onChange={(e) => setForm((prev) => ({ ...prev, body: e.target.value }))} rows={4} placeholder="Push notification body" />
	                  </div>

	                  <div className="space-y-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAF9] p-4">
	                    <div className="flex items-center gap-2">
	                      <SelectedVariantIcon className="h-4 w-4 text-[#C97A5E]" />
	                      <Label>Rich Content Style</Label>
	                    </div>
	                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
	                      {Object.entries(contentVariantMeta).map(([key, entry]) => {
	                        const Icon = entry.icon;
	                        const active = form.content_variant === key;
	                        return (
	                          <button
	                            key={key}
	                            type="button"
	                            onClick={() => setForm((prev) => ({ ...prev, content_variant: key }))}
	                            className={`rounded-xl border p-3 text-left transition ${
	                              active
	                                ? 'border-[#C97A5E] bg-[#FFF7F3] shadow-sm'
	                                : 'border-[#E5E5E5] bg-white hover:border-[#D8B1A2]'
	                            }`}
	                          >
	                            <div className="flex items-center gap-2">
	                              <Icon className={`h-4 w-4 ${active ? 'text-[#C97A5E]' : 'text-[#A8A8A8]'}`} />
	                              <span className="font-medium text-[#2D2420]">{entry.label}</span>
	                            </div>
	                            <p className="mt-2 text-xs text-[#7A6A64]">{entry.description}</p>
	                          </button>
	                        );
	                      })}
	                    </div>

	                    {(form.content_variant === 'image_cta' || form.content_variant === 'priority_alert') ? (
	                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
	                        <div className="space-y-2">
	                          <Label>{form.content_variant === 'image_cta' ? 'Image URL' : 'Banner Image URL (Optional)'}</Label>
	                          <Input
	                            value={form.image_url}
	                            onChange={(e) => setForm((prev) => ({ ...prev, image_url: e.target.value }))}
	                            placeholder="https://example.com/banner.jpg"
	                          />
	                        </div>
	                        <div className="space-y-2">
	                          <Label>CTA Label</Label>
	                          <Input
	                            value={form.cta_label}
	                            onChange={(e) => setForm((prev) => ({ ...prev, cta_label: e.target.value }))}
	                            placeholder="Open Order"
	                          />
	                        </div>
	                        <div className="space-y-2 md:col-span-2">
	                          <Label>CTA Destination</Label>
	                          <Select
	                            value={selectedCtaDestination}
	                            onValueChange={(value) => {
	                              if (value === '__custom__') {
	                                setForm((prev) => ({ ...prev, cta_screen: '' }));
	                                return;
	                              }
	                              setForm((prev) => ({ ...prev, cta_screen: value }));
	                            }}
	                          >
	                            <SelectTrigger>
	                              <SelectValue placeholder="Choose mobile screen path" />
	                            </SelectTrigger>
	                            <SelectContent>
	                              {ctaDestinationOptions.map((entry) => (
	                                <SelectItem key={entry.value} value={entry.value}>
	                                  {entry.label}
	                                </SelectItem>
	                              ))}
	                              <SelectItem value="__custom__">Custom Path</SelectItem>
	                            </SelectContent>
	                          </Select>
	                          <p className="text-xs text-[#7A6A64]">
	                            {selectedCtaDestinationMeta?.description || 'Choose a predefined mobile screen, or switch to custom path if needed.'}
	                          </p>
	                          {selectedCtaDestination === '__custom__' ? (
	                            <Input
	                              value={form.cta_screen}
	                              onChange={(e) => setForm((prev) => ({ ...prev, cta_screen: e.target.value }))}
	                              placeholder="/custom-screen-path"
	                            />
	                          ) : null}
	                        </div>
	                      </div>
	                    ) : null}
	                  </div>

	                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Payload Data</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setForm((prev) => ({ ...prev, data_payload: [...prev.data_payload, { key: '', value: '' }] }))}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Field
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {form.data_payload.map((row, index) => (
                        <div key={`payload-${index}`} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2">
                          <Input
                            value={row.key}
                            onChange={(e) => setForm((prev) => {
                              const next = [...prev.data_payload];
                              next[index] = { ...next[index], key: e.target.value };
                              return { ...prev, data_payload: next };
                            })}
                            placeholder="key"
                          />
                          <Input
                            value={row.value}
                            onChange={(e) => setForm((prev) => {
                              const next = [...prev.data_payload];
                              next[index] = { ...next[index], value: e.target.value };
                              return { ...prev, data_payload: next };
                            })}
                            placeholder="value"
                          />
                          <Button
                            variant="outline"
                            onClick={() => setForm((prev) => ({
                              ...prev,
                              data_payload: prev.data_payload.filter((_, rowIndex) => rowIndex !== index),
                            }))}
                            disabled={form.data_payload.length === 1}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-[#E5E5E5]">
                <CardHeader>
                  <CardTitle className="text-[#2D2420]">Audience Builder</CardTitle>
                  <CardDescription>Mix role-wide targets with specific admins, staff, and customers.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Roles</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setForm((prev) => ({ ...prev, roles: ['admin', 'sales_staff', 'customer'] }))}
                      >
                        All Users
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-4">
                      {['admin', 'sales_staff', 'customer'].map((role) => (
                        <label key={role} className="flex items-center gap-2 text-sm text-[#2D2420]">
                          <Checkbox
                            checked={form.roles.includes(role)}
                            onCheckedChange={(checked) => setForm((prev) => ({ ...prev, roles: arrayToggle(prev.roles, role, Boolean(checked)) }))}
                          />
                          <span>{role === 'sales_staff' ? 'All Sales Staff' : role === 'admin' ? 'All Admin Users' : 'Future Customer Audience'}</span>
                        </label>
                      ))}
                    </div>
                    {!customerDeviceLive ? (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                        Customer audience is modeled now for future use, but direct customer-device delivery is not live yet.
                      </p>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Specific Admins</Label>
                      <Input value={adminSearch} onChange={(e) => setAdminSearch(e.target.value)} placeholder="Search admins" />
                      <div className="border border-[#E5E5E5] rounded-md p-3 max-h-64 overflow-y-auto space-y-2">
                        {filteredAdmins.map((entry) => (
                          <label key={entry.id} className="flex items-start gap-2 text-sm">
                            <Checkbox
                              checked={form.admin_ids.includes(entry.id)}
                              onCheckedChange={(checked) => setForm((prev) => ({ ...prev, admin_ids: arrayToggle(prev.admin_ids, entry.id, Boolean(checked)) }))}
                            />
                            <span>
                              <span className="block font-medium text-[#2D2420]">{entry.name}</span>
                              <span className="block text-xs text-[#A8A8A8]">{entry.username} • {entry.role}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Specific Staff</Label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setForm((prev) => ({
                            ...prev,
                            staff_ids: Array.from(new Set([...(prev.staff_ids || []), ...filteredStaff.map((entry) => entry.id)])),
                          }))}
                        >
                          Select All Visible
                        </Button>
                      </div>
                      <Input value={staffSearch} onChange={(e) => setStaffSearch(e.target.value)} placeholder="Search staff" />
                      <div className="border border-[#E5E5E5] rounded-md p-3 max-h-64 overflow-y-auto space-y-2">
                        {filteredStaff.map((entry) => (
                          <label key={entry.id} className="flex items-start gap-2 text-sm">
                            <Checkbox
                              checked={form.staff_ids.includes(entry.id)}
                              onCheckedChange={(checked) => setForm((prev) => ({ ...prev, staff_ids: arrayToggle(prev.staff_ids, entry.id, Boolean(checked)) }))}
                            />
                            <span>
                              <span className="block font-medium text-[#2D2420]">{entry.name}</span>
                              <span className="block text-xs text-[#A8A8A8]">{entry.phone || 'No phone'} • {entry.area || 'No area'}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Specific Customers</Label>
                      <Input value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} placeholder="Search customers" />
                      <div className="border border-[#E5E5E5] rounded-md p-3 max-h-64 overflow-y-auto space-y-2">
                        {filteredCustomers.map((entry) => (
                          <label key={entry.id} className="flex items-start gap-2 text-sm">
                            <Checkbox
                              checked={form.customer_ids.includes(entry.id)}
                              onCheckedChange={(checked) => setForm((prev) => ({ ...prev, customer_ids: arrayToggle(prev.customer_ids, entry.id, Boolean(checked)) }))}
                            />
                            <span>
                              <span className="block font-medium text-[#2D2420]">{entry.name}</span>
                              <span className="block text-xs text-[#A8A8A8]">{entry.shop_name || 'No shop'} • {entry.sales_staff_name || 'Unassigned'}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center gap-2 text-sm text-[#2D2420]">
                      <Checkbox
                        checked={form.include_assigned_staff_for_customers}
                        onCheckedChange={(checked) => setForm((prev) => ({ ...prev, include_assigned_staff_for_customers: Boolean(checked) }))}
                      />
                      <span>Include assigned staff for selected customers</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-[#2D2420]">
                      <Checkbox
                        checked={form.include_admin_watchers}
                        onCheckedChange={(checked) => setForm((prev) => ({ ...prev, include_admin_watchers: Boolean(checked) }))}
                      />
                      <span>Include admin watchers</span>
                    </label>
                  </div>
                </CardContent>
              </Card>
            </div>

	            <div className="space-y-6">
	              <Card className="bg-white border-[#E5E5E5]">
	                <CardHeader>
	                  <CardTitle className="text-[#2D2420]">Live Preview</CardTitle>
	                  <CardDescription>How the richer notification will appear inside the mobile app.</CardDescription>
	                </CardHeader>
	                <CardContent>
	                  <div className={`overflow-hidden rounded-2xl border ${
	                    form.content_variant === 'priority_alert'
	                      ? 'border-amber-300 bg-amber-50'
	                      : 'border-[#E5E5E5] bg-white'
	                  }`}>
	                    {form.image_url ? (
	                      <img src={form.image_url} alt="Push preview" className="h-40 w-full object-cover" />
	                    ) : null}
	                    <div className="space-y-3 p-4">
	                      <div className="flex flex-wrap items-center gap-2">
	                        <Badge className="bg-[#FAFAF9] text-[#5C4033] border border-[#E5E5E5]">
	                          {contentVariantMeta[form.content_variant]?.label || 'Text Only'}
	                        </Badge>
	                        <Badge className="bg-[#FAFAF9] text-[#5C4033] border border-[#E5E5E5]">
	                          {pushTypeMap[form.push_type]?.label || form.push_type}
	                        </Badge>
	                      </div>
	                      <div>
	                        <p className="text-base font-semibold text-[#2D2420]">{form.title || 'Push title preview'}</p>
	                        <p className="mt-1 whitespace-pre-wrap text-sm text-[#5C4033]">{form.body || 'Push notification body preview'}</p>
	                      </div>
	                      {form.cta_label && (form.cta_screen || normalizePayloadRows(form.data_payload).screen) ? (
	                        <div>
	                          <span className="inline-flex rounded-full bg-[#C97A5E] px-4 py-2 text-sm font-semibold text-white">
	                            {form.cta_label}
	                          </span>
	                        </div>
	                      ) : null}
	                    </div>
	                  </div>
	                </CardContent>
	              </Card>

	              <Card className="bg-white border-[#E5E5E5]">
	                <CardHeader>
                  <CardTitle className="text-[#2D2420]">Audience Summary</CardTitle>
                  <CardDescription>Estimated targets and reachable device count.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {estimatingAudience ? (
                    <div className="flex items-center gap-2 text-[#A8A8A8]">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Calculating audience...
                    </div>
                  ) : audienceEstimate?.estimated_audience ? (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg border border-[#E5E5E5] p-3">
                          <p className="text-xs text-[#A8A8A8]">Logical Targets</p>
                          <p className="text-xl font-bold text-[#2D2420]">{audienceEstimate.estimated_audience.target_count || 0}</p>
                        </div>
                        <div className="rounded-lg border border-[#E5E5E5] p-3">
                          <p className="text-xs text-[#A8A8A8]">Reachable Tokens</p>
                          <p className="text-xl font-bold text-[#2D2420]">{audienceEstimate.estimated_audience.token_count || 0}</p>
                        </div>
                      </div>
                      <div className="rounded-lg bg-[#FAFAF9] border border-[#E5E5E5] p-3 text-sm text-[#5C4033]">
                        {audienceEstimate.estimated_audience.summary_text || 'No audience selected'}
                      </div>
                      {audienceEstimate.estimated_audience.customer_device_gap_count > 0 ? (
                        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700">
                          {audienceEstimate.estimated_audience.customer_device_gap_count} selected customer target(s) do not have live customer-app device delivery yet.
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="rounded-lg border border-dashed border-[#E5E5E5] p-4 text-sm text-[#A8A8A8]">
                      Select an audience to see the live estimate.
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white border-[#E5E5E5]">
                <CardHeader>
                  <CardTitle className="text-[#2D2420]">Actions</CardTitle>
                  <CardDescription>Save a draft or create and send now.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button variant="outline" className="w-full" onClick={handleSaveDraft} disabled={savingDraft || sendingNow}>
                    {savingDraft ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Save Draft
                  </Button>
                  <Button className="w-full bg-[#C97A5E] hover:bg-[#B06850]" onClick={handleCreateAndSend} disabled={savingDraft || sendingNow}>
                    {sendingNow ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Create and Send Now
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="delivery" className="space-y-6 mt-6">
          <Card className="bg-white border-[#E5E5E5]">
            <CardHeader>
              <CardTitle className="text-[#2D2420]">Delivery History</CardTitle>
              <CardDescription>Sent, failed, and partially delivered push campaigns.</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="w-6 h-6 animate-spin text-[#C97A5E]" />
                </div>
              ) : deliveryCampaigns.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-[#A8A8A8]">
                  <Megaphone className="w-5 h-5 mr-2" />
                  No sent campaigns yet
                </div>
              ) : (
                <div className="space-y-3">
                  {deliveryCampaigns.map((campaign) => (
                    <div key={campaign.id} className="border border-[#E5E5E5] rounded-lg p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
	                      <div>
	                        <div className="flex items-center gap-2">
	                          <h3 className="font-semibold text-[#2D2420]">{campaign.name}</h3>
	                          <Badge className={statusClasses[campaign.status] || 'bg-slate-100 text-slate-700'}>{campaign.status}</Badge>
	                          <Badge className="bg-[#FAFAF9] text-[#5C4033] border border-[#E5E5E5]">
	                            {contentVariantMeta[campaign.content_variant]?.label || 'Text Only'}
	                          </Badge>
	                        </div>
                        <p className="text-sm text-[#A8A8A8] mt-1">{campaign.title}</p>
                        <p className="text-xs text-[#A8A8A8] mt-1">{campaign.delivery_summary?.summary_text || '-'}</p>
                      </div>
                      <div className="text-sm text-[#5C4033]">
                        <div>Sent: {formatDateTime(campaign.sent_at)}</div>
                        <div>Success: {campaign.delivery_summary?.success_count || 0}</div>
                        <div>Failures: {campaign.delivery_summary?.failure_count || 0}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="types" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {(meta.push_types || []).map((entry) => (
              <Card key={entry.key} className="bg-white border-[#E5E5E5]">
                <CardHeader>
                  <CardTitle className="text-[#2D2420]">{entry.label}</CardTitle>
                  <CardDescription>{entry.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="text-sm">
                    <span className="font-medium text-[#2D2420]">Default title:</span>{' '}
                    <span className="text-[#A8A8A8]">{entry.default_title || '-'}</span>
                  </div>
	                  <div className="text-sm">
	                    <span className="font-medium text-[#2D2420]">Default body:</span>{' '}
	                    <span className="text-[#A8A8A8]">{entry.default_body || '-'}</span>
	                  </div>
	                  <div className="text-sm">
	                    <span className="font-medium text-[#2D2420]">Supported styles:</span>{' '}
	                    <span className="text-[#A8A8A8]">
	                      {(entry.supported_content_variants || []).map((variant) => contentVariantMeta[variant]?.label || variant).join(', ') || '-'}
	                    </span>
	                  </div>
	                  <div className="flex flex-wrap gap-2 pt-2">
                    {(entry.payload_hints || []).map((hint) => (
                      <Badge key={hint} className="bg-[#FAFAF9] text-[#5C4033] border border-[#E5E5E5]">{hint}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-[#2D2420]">Push Campaign Details</DialogTitle>
          </DialogHeader>
          {selectedCampaign ? (
            <div className="space-y-5">
	              <div className="flex items-center justify-between">
	                <div>
	                  <h3 className="text-lg font-semibold text-[#2D2420]">{selectedCampaign.name}</h3>
	                  <p className="text-sm text-[#A8A8A8]">
	                    {pushTypeMap[selectedCampaign.push_type]?.label || selectedCampaign.push_type}
	                    {' • '}
	                    {contentVariantMeta[selectedCampaign.content_variant]?.label || 'Text Only'}
	                  </p>
	                </div>
                <Badge className={statusClasses[selectedCampaign.status] || 'bg-slate-100 text-slate-700'}>{selectedCampaign.status}</Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
	                <Card className="border-[#E5E5E5]">
	                  <CardContent className="p-4 space-y-2">
	                    {selectedCampaign.image_url ? (
	                      <img src={selectedCampaign.image_url} alt="Campaign" className="h-40 w-full rounded-lg object-cover" />
	                    ) : null}
	                    <p className="text-xs text-[#A8A8A8]">Title</p>
	                    <p className="font-medium text-[#2D2420]">{selectedCampaign.title || '-'}</p>
	                    <p className="text-xs text-[#A8A8A8] pt-2">Body</p>
	                    <p className="text-sm text-[#2D2420] whitespace-pre-wrap">{selectedCampaign.body || '-'}</p>
	                    {selectedCampaign.cta_label ? (
	                      <>
	                        <p className="text-xs text-[#A8A8A8] pt-2">CTA</p>
	                        <p className="text-sm text-[#2D2420]">
	                          {selectedCampaign.cta_label}
	                          {selectedCampaign.cta_screen ? ` → ${selectedCampaign.cta_screen}` : ''}
	                        </p>
	                      </>
	                    ) : null}
	                  </CardContent>
	                </Card>
                <Card className="border-[#E5E5E5]">
                  <CardContent className="p-4 space-y-2">
                    <p className="text-xs text-[#A8A8A8]">Audience Summary</p>
                    <p className="text-sm text-[#2D2420]">{selectedCampaign.delivery_summary?.summary_text || selectedCampaign.estimated_audience?.summary_text || '-'}</p>
                    <div className="grid grid-cols-2 gap-2 pt-2 text-sm">
                      <div className="rounded-md bg-[#FAFAF9] p-2">
                        <p className="text-xs text-[#A8A8A8]">Targets</p>
                        <p className="font-semibold text-[#2D2420]">{selectedCampaign.delivery_summary?.target_count || selectedCampaign.estimated_audience?.target_count || 0}</p>
                      </div>
                      <div className="rounded-md bg-[#FAFAF9] p-2">
                        <p className="text-xs text-[#A8A8A8]">Tokens</p>
                        <p className="font-semibold text-[#2D2420]">{selectedCampaign.delivery_summary?.token_count || selectedCampaign.estimated_audience?.token_count || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-[#E5E5E5]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-[#2D2420]">Admins</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-[#5C4033] space-y-1 max-h-40 overflow-y-auto">
                    {(selectedCampaign.resolved_audience_snapshot?.selected_admins || []).length ? (
                      selectedCampaign.resolved_audience_snapshot.selected_admins.map((entry) => (
                        <p key={entry.id}>{entry.name}</p>
                      ))
                    ) : <p className="text-[#A8A8A8]">None</p>}
                  </CardContent>
                </Card>
                <Card className="border-[#E5E5E5]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-[#2D2420]">Staff</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-[#5C4033] space-y-1 max-h-40 overflow-y-auto">
                    {(selectedCampaign.resolved_audience_snapshot?.selected_staff || []).length ? (
                      selectedCampaign.resolved_audience_snapshot.selected_staff.map((entry) => (
                        <p key={entry.id}>{entry.name}</p>
                      ))
                    ) : <p className="text-[#A8A8A8]">None</p>}
                  </CardContent>
                </Card>
                <Card className="border-[#E5E5E5]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-[#2D2420]">Customers</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-[#5C4033] space-y-1 max-h-40 overflow-y-auto">
                    {(selectedCampaign.resolved_audience_snapshot?.selected_customers || []).length ? (
                      selectedCampaign.resolved_audience_snapshot.selected_customers.map((entry) => (
                        <p key={entry.id}>{entry.name}{entry.shop_name ? ` - ${entry.shop_name}` : ''}</p>
                      ))
                    ) : <p className="text-[#A8A8A8]">None</p>}
                  </CardContent>
                </Card>
              </div>

              <Card className="border-[#E5E5E5]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-[#2D2420]">Payload</CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(selectedCampaign.data_payload || {}).length ? (
                    <div className="space-y-2">
                      {Object.entries(selectedCampaign.data_payload || {}).map(([key, value]) => (
                        <div key={key} className="flex items-start justify-between gap-4 border-b border-[#F0F0F0] pb-2">
                          <span className="font-medium text-[#2D2420]">{key}</span>
                          <span className="text-sm text-[#5C4033] break-all text-right">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[#A8A8A8]">No payload data</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : null}
          <DialogFooter>
            {selectedCampaign?.status === 'draft' ? (
              <Button onClick={() => handleSendExisting(selectedCampaign.id)} disabled={sendingExistingId === selectedCampaign.id}>
                {sendingExistingId === selectedCampaign.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send Draft
              </Button>
            ) : null}
            {selectedCampaign?.status !== 'draft' ? (
              <Button onClick={() => handleResendCampaign(selectedCampaign.id)} disabled={resendingId === selectedCampaign.id}>
                {resendingId === selectedCampaign.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                Resend
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => setShowDetail(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
